import { useState, useRef, useEffect } from 'react';

import { useTheme, useMount } from '@simplywallst/ui-core';
import type { Theme } from '@simplywallst/ui-core';

import type { Holding } from '@/hooks/portfolio/usePortfolio';

type Args = {
  defaultId: Holding['id'] | null;
};

function getMediaQuery({ theme }: { theme: Theme }) {
  return typeof window === 'undefined'
    ? null
    : window.matchMedia(`(min-width: ${theme.lgMin})`);
}

export function usePanelHoldingId({ defaultId }: Args) {
  const theme = useTheme();
  const mediaQuery = useRef(getMediaQuery({ theme }));

  const [panelHoldingId, setPanelHoldingId] = useState<Holding['id'] | null>(
    () => {
      if (mediaQuery.current?.matches && defaultId) {
        return defaultId;
      }
      return null;
    }
  );

  useEffect(() => {
    if (panelHoldingId === null) return;
    setPanelHoldingId(defaultId);
  }, [defaultId, setPanelHoldingId]);

  useMount(() => {
    if (!mediaQuery.current) {
      mediaQuery.current = getMediaQuery({ theme });
    }

    const syncDefaultHolding = (media: { matches: boolean } | null) => {
      if (media?.matches && defaultId && panelHoldingId === null) {
        setPanelHoldingId(defaultId);
      }
    };

    // execute once 🥇
    syncDefaultHolding(mediaQuery.current);
    // listen for changes 👂
    mediaQuery.current?.addEventListener('change', syncDefaultHolding);
    // cleanup 🚮
    return () => {
      mediaQuery.current?.removeEventListener('change', syncDefaultHolding);
    };
  });

  return [panelHoldingId, setPanelHoldingId] as const;
}
